/**
 * Legal pages brief, item 5 (13 October 2026): the customer emails link to
 * the ÁSZF and the privacy notice in the customer's language.
 *
 * This replaces `TERMS_GAP`, the B14 placeholder that stood in the same place
 * in U1, U2, U5, U21 and U22 ("the link goes here once they are ready") — the
 * documents now exist, so the gap is filled where it was marked rather than
 * only in the two emails the brief names.
 *
 * The site address is the payload's (`public_site_url`, else the worker's
 * `PUBLIC_SITE_URL`). Without one there is nothing to link to, and the email
 * names that gap rather than printing a relative path a mail client cannot
 * open.
 */

import type { Block } from './layout.ts'
import { LEGAL_DOC_KEYS, LEGAL_DOCS, legalUrl, type LegalLang } from './legal.ts'
import type { EmailPayload, OrderPayload } from './payload.ts'

export function legalLinks(payload: EmailPayload, lang: LegalLang): Block[] {
  const site = (payload.site.site_url ?? '').trim()
  if (site === '') {
    return [
      {
        kind: 'note',
        text:
          lang === 'en'
            ? 'MISSING DATA: the site address is not set, so the Terms and Conditions and the Privacy Notice cannot be linked.'
            : 'HIÁNYZÓ ADAT: a webhely címe nincs beállítva, ezért az ÁSZF és az adatkezelési tájékoztató nem linkelhető.',
      },
    ]
  }
  return LEGAL_DOC_KEYS.map((doc) => ({
    kind: 'link' as const,
    label: LEGAL_DOCS[doc][lang].label,
    href: legalUrl(doc, lang, site),
  }))
}

/** U5: the ÁSZF travels as a PDF when the sender has attached one. The
 *  worker sets the flag, because it is the worker that knows whether a PDF
 *  of the order's recorded version exists. */
export function termsAttachedNote(order: OrderPayload, lang: LegalLang): Block | null {
  if (order.terms_pdf_attached !== true) return null
  return {
    kind: 'note',
    text:
      lang === 'en'
        ? 'The Terms and Conditions are attached to this email as a PDF.'
        : 'Az ÁSZF-et PDF-ben csatoltuk ehhez a levélhez.',
  }
}
